import { Helmet } from "react-helmet-async";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Eye, LogOut, Sparkles, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import type { Session } from "@supabase/supabase-js";

interface ImageEntry {
  src: string;
  alt: string;
  suggested: string;
  fullMatch: string;
}

const cleanFilenameForAlt = (src: string) => {
  try {
    const url = new URL(src);
    const name = url.pathname.split("/").pop() || src;
    const base = name.replace(/\.[^.]+$/, "");
    return base
      .replace(/[-_]+/g, " ")
      .replace(/\b\d{6,}\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return "";
  }
};

const extractImages = (html: string): ImageEntry[] => {
  if (!html) return [];
  const entries: ImageEntry[] = [];
  const imgRegex = /<img[^>]*>/gi;
  const matches = html.match(imgRegex) || [];
  matches.forEach((tag) => {
    const srcMatch = tag.match(/src=["']([^"']+)["']/i);
    const altMatch = tag.match(/alt=["']([^"']*)["']/i);
    if (!srcMatch) return;
    entries.push({
      src: srcMatch[1],
      alt: altMatch ? altMatch[1] : "",
      suggested: "",
      fullMatch: tag,
    });
  });
  return entries;
};

const findNearestHeadingBefore = (html: string, imgTag: string): string => {
  const idx = html.indexOf(imgTag);
  if (idx === -1) return "";
  const before = html.slice(0, idx);
  const headingRegex = /<h[1-3][^>]*>([\s\S]*?)<\/h[1-3]>/gi;
  let match: RegExpExecArray | null;
  let last = "";
  while ((match = headingRegex.exec(before)) !== null) {
    last = match[1].replace(/<[^>]*>/g, "").trim();
  }
  return last;
};

const updateImgAlt = (html: string, fullMatch: string, newAlt: string): string => {
  const escapedAlt = newAlt.replace(/"/g, "&quot;");
  let replacement: string;
  if (/alt=["'][^"']*["']/i.test(fullMatch)) {
    replacement = fullMatch.replace(/alt=["'][^"']*["']/i, `alt="${escapedAlt}"`);
  } else {
    replacement = fullMatch.replace(/<img/i, `<img alt="${escapedAlt}"`);
  }
  return html.replace(fullMatch, replacement);
};

interface PostRow {
  id: string;
  title: string;
  slug: string;
  status: string;
  published_at: string | null;
  created_at: string;
  content?: string | null;
  featured_image?: string | null;
}

const AltTextDialog = ({
  open,
  onOpenChange,
  post,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  post: PostRow | null;
}) => {
  const queryClient = useQueryClient();
  const [images, setImages] = useState<ImageEntry[]>([]);

  useEffect(() => {
    if (post?.content) {
      const imgs = extractImages(post.content);
      imgs.forEach((img) => {
        const filenamePart = cleanFilenameForAlt(img.src);
        const heading = findNearestHeadingBefore(post.content || "", img.fullMatch);
        const parts = [filenamePart, heading, post.title].filter(Boolean);
        const seen = new Set<string>();
        const unique = parts.filter((p) => {
          const k = p.toLowerCase();
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        const suggestion = unique.join(" – ");
        img.suggested = suggestion.charAt(0).toUpperCase() + suggestion.slice(1);
      });
      setImages(imgs);
    } else {
      setImages([]);
    }
  }, [post]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!post) return;
      let updated = post.content || "";
      images.forEach((img) => {
        updated = updateImgAlt(updated, img.fullMatch, img.alt);
      });
      const { error } = await supabase
        .from("blog_posts")
        .update({ content: updated })
        .eq("id", post.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Alt text updated");
      queryClient.invalidateQueries({ queryKey: ["admin-blog-posts"] });
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const applySuggestion = (idx: number) => {
    setImages((prev) =>
      prev.map((img, i) => (i === idx ? { ...img, alt: img.suggested } : img))
    );
  };

  const applyAllSuggestions = () => {
    setImages((prev) => prev.map((img) => ({ ...img, alt: img.alt || img.suggested })));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fix image alt text</DialogTitle>
          <DialogDescription>
            Review and edit alt text for every image in this post. Use the suggestion button
            to auto-fill from the filename and the nearest heading.
          </DialogDescription>
        </DialogHeader>

        {images.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No images found in this post.
          </p>
        ) : (
          <>
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={applyAllSuggestions} className="gap-2">
                <Sparkles size={14} />
                Apply all suggestions to empty fields
              </Button>
            </div>
            <div className="space-y-4">
              {images.map((img, idx) => {
                const missing = !img.alt.trim();
                return (
                  <div
                    key={idx}
                    className={`flex gap-3 p-3 rounded border ${missing ? "border-destructive/50" : "border-border"}`}
                  >
                    <img
                      src={img.src}
                      alt=""
                      className="w-24 h-24 object-cover rounded border border-border flex-shrink-0"
                    />
                    <div className="flex-1 space-y-2 min-w-0">
                      <Input
                        value={img.alt}
                        placeholder={missing ? "Missing — add alt text" : ""}
                        onChange={(e) =>
                          setImages((prev) =>
                            prev.map((it, i) => (i === idx ? { ...it, alt: e.target.value } : it))
                          )
                        }
                      />
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => applySuggestion(idx)}
                          className="gap-2"
                          disabled={!img.suggested}
                        >
                          <Sparkles size={14} />
                          Suggest
                        </Button>
                        {img.suggested && (
                          <span className="text-xs text-muted-foreground truncate">
                            {img.suggested}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || images.length === 0}>
            {saveMutation.isPending ? "Saving..." : "Save alt text"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BlogAdmin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [altPost, setAltPost] = useState<PostRow | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const { data: posts, isLoading: postsLoading } = useQuery({
    queryKey: ["admin-blog-posts"],
    enabled: !!session,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, status, published_at, created_at, content, featured_image")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as PostRow[];
    },
  });

  const postsWithImageCounts = useMemo(() => {
    return (posts || []).map((p) => {
      const imgs = extractImages(p.content || "");
      const missing = imgs.filter((i) => !i.alt.trim()).length;
      const isScheduled =
        p.status === "published" && p.published_at && new Date(p.published_at) > new Date();
      return { ...p, imgCount: imgs.length, missingAlt: missing, isScheduled };
    });
  }, [posts]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("blog_posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blog-posts"] });
      toast.success("Post deleted");
    },
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast.error(error.message);
    }
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </main>
        <Footer />
      </>
    );
  }

  if (!session) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-24 pb-16 flex items-center justify-center">
          <form onSubmit={handleLogin} className="w-full max-w-sm space-y-4 bg-card p-8 rounded-lg border border-border">
            <h1 className="text-2xl font-bold text-center">Admin Login</h1>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit" className="w-full" disabled={authLoading}>
              {authLoading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </main>
        <Footer />
      </>
    );
  }

  const formatScheduled = (iso: string | null) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString();
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Blog Admin | Cetasol</title>
      </Helmet>
      <Header />
      <main className="min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold">Blog Admin</h1>
            <div className="flex gap-3">
              <Button onClick={() => navigate("/admin/blog/new")} className="gap-2">
                <Plus size={16} />
                New Post
              </Button>
              <Button variant="ghost" onClick={handleLogout} className="gap-2">
                <LogOut size={16} />
              </Button>
            </div>
          </div>

          {postsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-card rounded-lg border border-border animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {postsWithImageCounts.map((post) => (
                <div
                  key={post.id}
                  className="flex items-center justify-between p-4 bg-card rounded-lg border border-border"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{post.title}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {post.isScheduled ? (
                        <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40">
                          Scheduled · {formatScheduled(post.published_at)}
                        </Badge>
                      ) : (
                        <Badge variant={post.status === "published" ? "default" : "secondary"}>
                          {post.status}
                        </Badge>
                      )}
                      {post.imgCount > 0 && post.missingAlt > 0 && (
                        <Badge variant="destructive" className="gap-1">
                          <ImageIcon size={12} />
                          {post.missingAlt} missing alt
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    {post.imgCount > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Fix image alt text"
                        onClick={() => setAltPost(post)}
                      >
                        <Sparkles size={16} />
                      </Button>
                    )}
                    {post.status === "published" && !post.isScheduled && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/${post.slug}`)}
                      >
                        <Eye size={16} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm("Delete this post?")) {
                          deleteMutation.mutate(post.id);
                        }
                      }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />

      <AltTextDialog
        open={!!altPost}
        onOpenChange={(o) => !o && setAltPost(null)}
        post={altPost}
      />
    </>
  );
};

export default BlogAdmin;
